/**
 * WHAT: Generic, technology-agnostic password policy compliance dashboard.
 * WHY: Oracle, MSSQL, MySQL, and Cassandra share the same compliance workflow (profiles,
 *      CIS benchmarks, user compliance, overrides) -- this single component adapts via a
 *      technology prop, eliminating per-database code duplication.
 */

import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { WideModal, PostgresqlIcon } from '../common';

/**
 * WHAT: Maps each database technology to its display config (icon, colors, column headers, feature flags).
 * WHY: The generic compliance component adapts its rendering per technology; this config drives those differences
 *      without duplicating component logic for each database type.
 */
const TECH_CONFIG = {
  oracle: {
    name: 'Oracle',
    icon: 'fa-database',
    color: 'text-danger',
    bgColor: 'bg-danger',
    profileColumns: ['Profile Name', 'Database', 'Host', 'Verify Function', 'Life Time', 'Reuse Policy', 'Failed Attempts'],
    hasComplexityRules: true
  },
  mssql: {
    name: 'SQL Server',
    icon: 'fa-server',
    color: 'text-primary',
    bgColor: 'bg-primary',
    profileColumns: ['Login Name', 'Server', 'Policy Checked', 'Expiration Checked', 'Type', 'Status'],
    hasComplexityRules: false
  },
  mysql: {
    name: 'MySQL',
    icon: 'fa-database',
    color: 'text-info',
    bgColor: 'bg-info',
    profileColumns: ['Variable', 'Value', 'Plugin Status'],
    hasComplexityRules: true
  },
  postgresql: {
    name: 'PostgreSQL',
    iconComponent: PostgresqlIcon,
    color: 'text-primary',
    bgColor: 'bg-primary',
    profileColumns: ['Role Name', 'Database', 'Valid Until', 'Extensions', 'Constraints'],
    hasComplexityRules: false
  },
  cassandra: {
    name: 'Cassandra',
    icon: 'fa-cubes',
    color: 'text-success',
    bgColor: 'bg-success',
    profileColumns: ['Role Name', 'Cluster', 'Note'],
    hasComplexityRules: false
  }
};

/**
 * WHAT: Generic password policy compliance dashboard that adapts to any supported database technology.
 * WHY: Oracle, MSSQL, MySQL, PostgreSQL, and Cassandra share the same compliance workflow (profiles, benchmarks,
 *      overrides, user compliance) -- this single component avoids duplicating hundreds of lines per technology.
 */
const PasswordPolicyCompliance = ({ technology }) => {
  const { canViewComplianceReports } = useAuth();
  const config = TECH_CONFIG[technology] || TECH_CONFIG.oracle;

  // State for data
  const [profiles, setProfiles] = useState([]);
  const [complianceResults, setComplianceResults] = useState(null);
  const [benchmarkRules, setBenchmarkRules] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [userCompliance, setUserCompliance] = useState(null);
  const [expandedAccountTypes, setExpandedAccountTypes] = useState(['named']);

  // State for UI
  const [activeTab, setActiveTab] = useState('profiles');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assessmentRunning, setAssessmentRunning] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [comparisonMode, setComparisonMode] = useState('cis'); // 'cis' or 'custom'
  const [selectedUserCompliance, setSelectedUserCompliance] = useState(null);

  // Override form state
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideForm, setOverrideForm] = useState({
    rule_id: '',
    override_type: 'SUPPRESS',
    custom_expected_value: '',
    account_types: ['all'],
    justification: '',
    expiry_date: ''
  });
  const [editingOverride, setEditingOverride] = useState(null);

  // Available account types for policies
  const accountTypeOptions = [
    { value: 'all', label: 'All Accounts', description: 'Applies to all account types' },
    { value: 'named', label: 'Named Accounts', description: 'Individual user accounts (human users)' },
    { value: 'application', label: 'Application Accounts', description: 'Accounts used by applications' },
    { value: 'system', label: 'System Accounts', description: 'Built-in database system accounts' },
    { value: 'service', label: 'Service Accounts', description: 'Accounts for background services' },
    { value: 'privileged', label: 'Privileged Accounts', description: 'Accounts with elevated privileges (DBA, SYSDBA)' }
  ];

  // Check permissions
  const hasAccess = canViewComplianceReports(technology);

  /* WHAT: Fetches password profile definitions for the current technology.
     WHY: Profiles are the first tab and provide the raw data that compliance checks evaluate against benchmarks. */
  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/api/databases/compliance/${technology}/profiles/`);
      if (response.data.success) {
        setProfiles(response.data.profiles || []);
      }
    } catch (err) {
      setError(`Failed to fetch ${config.name} profiles`);
      console.error('Error fetching profiles:', err);
    } finally {
      setLoading(false);
    }
  }, [technology, config.name]);

  /* WHAT: Fetches per-rule compliance assessment results for the current technology.
     WHY: Results show which password policies pass or fail against CIS or custom benchmarks. */
  const fetchComplianceResults = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/api/databases/compliance/${technology}/results/`);
      if (response.data.success) {
        setComplianceResults(response.data);
      }
    } catch (err) {
      setError('Failed to fetch compliance results');
      console.error('Error fetching compliance results:', err);
    } finally {
      setLoading(false);
    }
  }, [technology]);

  /* WHAT: Fetches CIS benchmark rule definitions (expected values, severity, category).
     WHY: Rules are displayed alongside results so users can understand what each compliance check requires. */
  const fetchBenchmarkRules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/api/databases/compliance/${technology}/rules/`);
      if (response.data.success) {
        setBenchmarkRules(response.data.rules || []);
      }
    } catch (err) {
      console.error('Error fetching benchmark rules:', err);
    } finally {
      setLoading(false);
    }
  }, [technology]);

  /* WHAT: Fetches custom compliance overrides (suppress or custom-value rules).
     WHY: Organizations may have legitimate deviations from CIS benchmarks; overrides document and track those exceptions. */
  const fetchOverrides = useCallback(async () => {
    try {
      const response = await apiClient.get(`/api/databases/compliance/${technology}/overrides/`);
      if (response.data.success) {
        setOverrides(response.data.overrides || []);
      }
    } catch (err) {
      console.error('Error fetching overrides:', err);
    }
  }, [technology]);

  /* WHAT: Fetches per-user compliance data grouped by account type.
     WHY: User-level view shows which individual accounts are non-compliant, enabling targeted remediation. */
  const fetchUserCompliance = useCallback(async () => {
    try {
      const response = await apiClient.get(`/api/databases/compliance/${technology}/user-compliance/`);
      if (response.data.success) {
        setUserCompliance(response.data);
      }
    } catch (err) {
      console.error('Error fetching user compliance:', err);
    }
  }, [technology]);

  /* WHAT: Triggers a new compliance assessment run on the backend for the current technology.
     WHY: Assessments must be re-run after profile changes or data refreshes to get current compliance status. */
  const runAssessment = async () => {
    setAssessmentRunning(true);
    setError(null);
    try {
      const response = await apiClient.post(`/api/databases/compliance/${technology}/assess/`);
      if (response.data.success) {
        await fetchComplianceResults();
        setActiveTab('results');
      } else {
        setError(response.data.error || 'Assessment failed');
      }
    } catch (err) {
      setError('Failed to run assessment');
      console.error('Error running assessment:', err);
    } finally {
      setAssessmentRunning(false);
    }
  };

  /* WHAT: Creates or updates a compliance override (suppress rule or set custom expected value).
     WHY: Admins need to document exceptions to CIS benchmarks with justification and optional expiry for audit trails. */
  const saveOverride = async () => {
    try {
      const payload = {
        ...overrideForm,
        database_type: config.name
      };

      if (editingOverride) {
        await apiClient.put(`/api/databases/compliance/${technology}/overrides/${editingOverride.id}/`, payload);
      } else {
        await apiClient.post(`/api/databases/compliance/${technology}/overrides/`, payload);
      }

      setShowOverrideModal(false);
      setEditingOverride(null);
      setOverrideForm({
        rule_id: '',
        override_type: 'SUPPRESS',
        custom_expected_value: '',
        account_types: ['all'],
        justification: '',
        expiry_date: ''
      });
      await fetchOverrides();
    } catch (err) {
      setError('Failed to save override');
      console.error('Error saving override:', err);
    }
  };

  /* WHAT: Deletes a compliance override after user confirmation.
     WHY: Removing an override restores the original CIS benchmark rule for standard compliance checking. */
  const deleteOverride = async (overrideId) => {
    if (!window.confirm('Are you sure you want to delete this override?')) return;

    try {
      await apiClient.delete(`/api/databases/compliance/${technology}/overrides/${overrideId}/`);
      await fetchOverrides();
    } catch (err) {
      setError('Failed to delete override');
      console.error('Error deleting override:', err);
    }
  };

  // Load data on mount
  useEffect(() => {
    if (hasAccess) {
      fetchProfiles();
      fetchComplianceResults();
      fetchBenchmarkRules();
      fetchOverrides();
      fetchUserCompliance();
    }
  }, [hasAccess, fetchProfiles, fetchComplianceResults, fetchBenchmarkRules, fetchOverrides, fetchUserCompliance]);

  // Permission denied view
  if (!hasAccess) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">
          <i className="fas fa-lock me-2"></i>
          You don't have permission to view {config.name} compliance reports.
          Please contact your administrator if you need access.
        </div>
      </div>
    );
  }

  /* WHAT: Safely parses a value that may be a JSON string, object, or null into a plain object.
     WHY: Backend returns some fields as JSON strings and others as objects; this normalizes both forms. */
  const parseJSON = (value) => {
    if (!value) return {};
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }
    return value;
  };

  /* WHAT: Converts a benchmark expected-value object (operator + value) into human-readable text like "at least 10".
     WHY: CIS rules store expected values as {operator, value} JSON; users need plain-language descriptions in the UI. */
  const formatExpectedValue = (value) => {
    if (value === null || value === undefined) return 'Not specified';

    let parsed = value;
    if (typeof value === 'string') {
      try {
        parsed = JSON.parse(value);
      } catch {
        return value;
      }
    }

    if (typeof parsed !== 'object') return String(parsed);

    const operator = parsed.operator || '==';
    const val = parsed.value;

    // Map operators to readable text
    const operatorMap = {
      '>=': 'at least',
      '<=': 'at most',
      '>': 'more than',
      '<': 'less than',
      '==': 'exactly',
      '!=': 'not',
      'in': 'one of',
      'not_in': 'not one of',
      'contains': 'contains',
      'not_null': 'must be configured',
      'regex': 'matches pattern'
    };

    const readableOp = operatorMap[operator] || operator;

    // Handle special cases
    if (operator === 'not_null') {
      return 'Must be configured (not NULL)';
    }

    if (operator === 'in' && Array.isArray(val)) {
      return `${readableOp}: ${val.join(', ')}`;
    }

    if (val === undefined || val === null) {
      return readableOp;
    }

    return `${readableOp} ${val}`;
  };

  /* WHAT: Extracts a displayable string from an actual compliance value (primitive, JSON string, or {value} object).
     WHY: Actual values come in multiple shapes from different database profile parameters; this normalizes for display. */
  const formatActualValue = (value) => {
    if (value === null || value === undefined) return null;

    // If it's already a primitive, return as string
    if (typeof value !== 'object') return String(value);

    // If it's a string that might be JSON, try to parse
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch {
        return value;
      }
    }

    // If it's an object with a 'value' key, extract that
    if (value && typeof value === 'object') {
      if ('value' in value) return String(value.value);
      // Otherwise stringify it nicely
      return JSON.stringify(value);
    }

    return String(value);
  };

  /* WHAT: Evaluates whether an actual profile value meets a custom override's expected value using operator logic.
     WHY: When admins set custom thresholds instead of CIS defaults, the UI re-evaluates compliance client-side. */
  const checkCustomCompliance = (actualValue, customExpectedValue) => {
    if (actualValue === null || actualValue === undefined) return false;

    let expected;
    try {
      expected = typeof customExpectedValue === 'string' ? JSON.parse(customExpectedValue) : customExpectedValue;
    } catch {
      return String(actualValue) === String(customExpectedValue);
    }

    if (!expected || typeof expected !== 'object') {
      return String(actualValue) === String(customExpectedValue);
    }

    const operator = expected.operator || '==';
    const expectedVal = expected.value;
    const actual = typeof actualValue === 'object' && 'value' in actualValue ? actualValue.value : actualValue;
    const actualNum = parseFloat(actual);
    const expectedNum = parseFloat(expectedVal);

    switch (operator) {
      case '>=': return !isNaN(actualNum) && !isNaN(expectedNum) && actualNum >= expectedNum;
      case '<=': return !isNaN(actualNum) && !isNaN(expectedNum) && actualNum <= expectedNum;
      case '>': return !isNaN(actualNum) && !isNaN(expectedNum) && actualNum > expectedNum;
      case '<': return !isNaN(actualNum) && !isNaN(expectedNum) && actualNum < expectedNum;
      case '==': return String(actual) === String(expectedVal);
      case '!=': return String(actual) !== String(expectedVal);
      case 'not_null': return actual !== null && actual !== undefined && actual !== 'NULL';
      default: return String(actual) === String(expectedVal);
    }
  };

  /* WHAT: Renders a severity level (CRITICAL/HIGH/MEDIUM/LOW) as colored text with a dot indicator.
     WHY: Consistent severity rendering across compliance tables following the DAMA text-color-over-badge styling standard. */
  const renderSeverityBadge = (severity) => {
    const styleMap = {
      'CRITICAL': 'text-danger',
      'HIGH': 'text-warning',
      'MEDIUM': 'text-info',
      'LOW': 'text-secondary'
    };
    const labelMap = {
      'CRITICAL': 'Critical',
      'HIGH': 'High',
      'MEDIUM': 'Medium',
      'LOW': 'Low'
    };
    return (
      <span className={styleMap[severity] || 'text-secondary'}>
        <i className="fas fa-circle fa-xs me-1"></i>
        {labelMap[severity] || severity}
      </span>
    );
  };

  /* WHAT: Renders a single profile table row with technology-specific columns (Oracle profiles vs MSSQL logins vs MySQL variables).
     WHY: Each database technology stores password profiles differently; this switch renders the appropriate columns per technology. */
  const renderProfileRow = (profile, idx) => {
    const passwordPolicy = parseJSON(profile.password_policy);

    switch (technology) {
      case 'oracle':
        return (
          <tr key={idx}>
            <td><strong>{profile.profile_name}</strong></td>
            <td>{profile.db_name || profile.database_identifier}</td>
            <td>{profile.host_name}</td>
            <td>
              {passwordPolicy.password_verify_function === 'NULL' ? (
                <span className="text-danger">None</span>
              ) : (
                <span className="text-success">{passwordPolicy.password_verify_function}</span>
              )}
            </td>
            <td>{passwordPolicy.password_life_time || 'N/A'}</td>
            <td>Max: {passwordPolicy.password_reuse_max || 'N/A'}, Time: {passwordPolicy.password_reuse_time || 'N/A'}</td>
            <td>{passwordPolicy.failed_login_attempts || 'N/A'}</td>
            <td>
              <button className="btn btn-sm btn-outline-primary" onClick={() => setSelectedProfile(profile)}>
                <i className="fas fa-eye"></i>
              </button>
            </td>
          </tr>
        );

      case 'mssql':
        return (
          <tr key={idx}>
            <td><strong>{profile.login_name || profile.profile_name}</strong></td>
            <td>{profile.server_name || profile.host_name}</td>
            <td>
              {passwordPolicy.is_policy_checked ? (
                <span className="text-success">Yes</span>
              ) : (
                <span className="text-danger">No</span>
              )}
            </td>
            <td>
              {passwordPolicy.is_expiration_checked ? (
                <span className="text-success">Yes</span>
              ) : (
                <span className="text-warning">No</span>
              )}
            </td>
            <td>{passwordPolicy.type_desc || 'SQL_LOGIN'}</td>
            <td>
              {passwordPolicy.is_disabled ? (
                <span className="text-secondary">Disabled</span>
              ) : (
                <span className="text-success">Active</span>
              )}
            </td>
          </tr>
        );

      case 'mysql':
        return (
          <tr key={idx}>
            <td><strong>{profile.variable_name || profile.profile_name}</strong></td>
            <td><code>{profile.variable_value || passwordPolicy.value || 'N/A'}</code></td>
            <td>
              {passwordPolicy.plugin_loaded ? (
                <span className="text-success">Loaded</span>
              ) : (
                <span className="text-warning">Not Loaded</span>
              )}
            </td>
          </tr>
        );

      case 'postgresql':
        return (
          <tr key={idx}>
            <td><strong>{profile.role_name || profile.profile_name}</strong></td>
            <td>{profile.db_name || profile.database_identifier}</td>
            <td>{profile.valuntil || passwordPolicy.valid_until || 'Never'}</td>
            <td>
              {passwordPolicy.extensions && passwordPolicy.extensions.length > 0 ? (
                passwordPolicy.extensions.map((ext, i) => (
                  <span key={i} className="text-info me-1">{ext}</span>
                ))
              ) : (
                <span className="text-muted">None</span>
              )}
            </td>
            <td>
              {passwordPolicy.connection_limit >= 0 ? (
                <span className="text-secondary">Limit: {passwordPolicy.connection_limit}</span>
              ) : (
                <span className="text-muted">Unlimited</span>
              )}
            </td>
          </tr>
        );

      case 'cassandra':
        return (
          <tr key={idx}>
            <td><strong>{profile.role_name || profile.profile_name}</strong></td>
            <td>{profile.cluster_name || profile.host_name}</td>
            <td>
              <span className="text-muted fst-italic">
                Cassandra has no built-in password policies
              </span>
            </td>
          </tr>
        );

      default:
        return (
          <tr key={idx}>
            <td>{profile.profile_name}</td>
            <td colSpan={config.profileColumns.length - 1}>
              <pre className="mb-0 small">{JSON.stringify(passwordPolicy, null, 2)}</pre>
            </td>
          </tr>
        );
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            {config.iconComponent ? (
              <config.iconComponent size={28} className="me-2" />
            ) : (
              <i className={`fas ${config.icon} me-2 ${config.color}`}></i>
            )}
            {config.name} Password Policy Compliance
          </h2>
          <p className="text-muted mb-0">
            Review password profiles and CIS benchmark compliance
          </p>
        </div>
        {technology !== 'cassandra' && (
          <button
            className="btn btn-primary"
            onClick={runAssessment}
            disabled={assessmentRunning}
          >
            {assessmentRunning ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Running Assessment...
              </>
            ) : (
              <>
                <i className="fas fa-play me-2"></i>
                Run Assessment
              </>
            )}
          </button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Cassandra Notice */}
      {technology === 'cassandra' && (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          <strong>Note:</strong> Apache Cassandra does not have built-in password policy features.
          Password complexity and expiration must be enforced at the application level or through
          external authentication (LDAP/Kerberos).
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'profiles' ? 'active' : ''}`}
            onClick={() => setActiveTab('profiles')}
          >
            <i className="fas fa-user-shield me-2"></i>
            {technology === 'cassandra' ? 'Roles' : 'Profiles'} ({profiles.length})
          </button>
        </li>
        {technology !== 'cassandra' && (
          <>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'results' ? 'active' : ''}`}
                onClick={() => setActiveTab('results')}
              >
                <i className="fas fa-clipboard-check me-2"></i>
                Compliance Results
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'rules' ? 'active' : ''}`}
                onClick={() => setActiveTab('rules')}
              >
                <i className="fas fa-book me-2"></i>
                Benchmark Rules ({benchmarkRules.length})
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'overrides' ? 'active' : ''}`}
                onClick={() => setActiveTab('overrides')}
              >
                <i className="fas fa-sliders-h me-2"></i>
                Custom Policies ({overrides.length})
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                <i className="fas fa-users me-2"></i>
                User Compliance {userCompliance?.count ? `(${userCompliance.count})` : ''}
              </button>
            </li>
          </>
        )}
      </ul>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Profiles Tab */}
      {activeTab === 'profiles' && !loading && (
        <div className="card">
          <div className="card-header bg-light border-bottom">
            <h5 className="mb-0">
              <i className="fas fa-user-shield me-2"></i>
              {config.name} {technology === 'cassandra' ? 'Roles' : 'Password Profiles'}
            </h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    {config.profileColumns.map((col, idx) => (
                      <th key={idx}>{col}</th>
                    ))}
                    {technology !== 'cassandra' && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {profiles.length === 0 ? (
                    <tr>
                      <td colSpan={config.profileColumns.length + 1} className="text-center py-4 text-muted">
                        <i className="fas fa-info-circle me-2"></i>
                        No profiles found. Run a profile collection first.
                      </td>
                    </tr>
                  ) : (
                    profiles.map((profile, idx) => renderProfileRow(profile, idx))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Results Tab */}
      {activeTab === 'results' && !loading && complianceResults && (
        <div className="card">
          <div className="card-header bg-light border-bottom d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-clipboard-check me-2"></i>
              Compliance Assessment Results
            </h5>
            <div className="btn-group">
              <button
                className={`btn btn-sm ${comparisonMode === 'cis' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => setComparisonMode('cis')}
              >
                <i className="fas fa-book me-1"></i>Compare with CIS Benchmark
              </button>
              <button
                className={`btn btn-sm ${comparisonMode === 'custom' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => setComparisonMode('custom')}
                title="Compare with your custom policies"
              >
                <i className="fas fa-sliders-h me-1"></i>Compare with Custom Policies
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th>Rule Code</th>
                    <th>Rule Name</th>
                    <th>Database</th>
                    <th>Host</th>
                    <th>Profile</th>
                    <th>{comparisonMode === 'cis' ? 'CIS Expected' : 'Custom Expected'}</th>
                    <th>Profile Actual</th>
                    <th>Severity</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(complianceResults.results || []).map((result, idx) => {
                    // Get custom policy for this rule if in custom mode
                    const customPolicy = comparisonMode === 'custom'
                      ? overrides.find(o => o.rule_id === result.rule_id || o.rule_code === result.rule_code)
                      : null;

                    // Determine expected value based on comparison mode
                    const expectedValue = comparisonMode === 'custom' && customPolicy
                      ? (customPolicy.override_type === 'SUPPRESS' ? 'Suppressed' : customPolicy.custom_expected_value)
                      : result.expected_value;

                    // Determine compliance based on comparison mode
                    const isCompliant = comparisonMode === 'custom' && customPolicy
                      ? (customPolicy.override_type === 'SUPPRESS' ? true : checkCustomCompliance(result.actual_value, customPolicy.custom_expected_value))
                      : result.is_compliant;

                    // Skip if in custom mode and no custom policy exists for this rule
                    if (comparisonMode === 'custom' && !customPolicy) {
                      return null;
                    }

                    return (
                      <tr key={idx} style={!isCompliant ? { borderLeft: '3px solid #dc3545' } : {}}>
                        <td><code>{result.rule_code}</code></td>
                        <td>{result.rule_name}</td>
                        <td>{result.database_name || result.db_name || '-'}</td>
                        <td>{result.host_name || result.host || '-'}</td>
                        <td>{result.profile_name}</td>
                        <td>{customPolicy?.override_type === 'SUPPRESS' ? <span className="text-muted fst-italic">Suppressed</span> : formatExpectedValue(expectedValue)}</td>
                        <td>{formatActualValue(result.actual_value) === null ? <span className="text-muted">NULL</span> : formatActualValue(result.actual_value)}</td>
                        <td>{renderSeverityBadge(result.severity)}</td>
                        <td>
                          {isCompliant ? (
                            <span className="text-success">
                              <i className="fas fa-check me-1"></i>Compliant
                            </span>
                          ) : (
                            <span className="text-danger">
                              <i className="fas fa-times me-1"></i>Non-Compliant
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {comparisonMode === 'custom' && overrides.length === 0 && (
                    <tr>
                      <td colSpan="9" className="text-center py-4 text-muted">
                        <i className="fas fa-info-circle me-2"></i>
                        No custom policies defined. Please add custom policies in the "Custom Policies" tab first.
                      </td>
                    </tr>
                  )}
                  {comparisonMode === 'custom' && overrides.length > 0 &&
                    (complianceResults.results || []).filter(r => overrides.find(o => o.rule_id === r.rule_id || o.rule_code === r.rule_code)).length === 0 && (
                    <tr>
                      <td colSpan="9" className="text-center py-4 text-muted">
                        <i className="fas fa-info-circle me-2"></i>
                        No matching results for your custom policies.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Benchmark Rules Tab */}
      {activeTab === 'rules' && !loading && (
        <div className="card">
          <div className="card-header bg-light border-bottom">
            <h5 className="mb-0">
              <i className="fas fa-book me-2"></i>
              CIS Benchmark Rules
            </h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th>Rule Code</th>
                    <th>Rule Name</th>
                    <th>Category</th>
                    <th>Expected Value</th>
                    <th>Severity</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {benchmarkRules.map((rule, idx) => (
                    <tr key={idx}>
                      <td><code>{rule.rule_code}</code></td>
                      <td>{rule.rule_name}</td>
                      <td>{rule.rule_category}</td>
                      <td>{formatExpectedValue(rule.expected_value)}</td>
                      <td>{renderSeverityBadge(rule.severity)}</td>
                      <td className="small">{rule.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Custom Policies Tab */}
      {activeTab === 'overrides' && !loading && (
        <div className="card">
          <div className="card-header bg-light border-bottom d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-sliders-h me-2"></i>
              Custom Policies
            </h5>
            <button className="btn btn-sm btn-light" onClick={() => setShowOverrideModal(true)}>
              <i className="fas fa-plus me-2"></i>Add Custom Policy
            </button>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th>Rule</th>
                    <th>Type</th>
                    <th>Custom Value</th>
                    <th>Applicable To</th>
                    <th>Justification</th>
                    <th>Expiry</th>
                    <th>Created By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {overrides.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        <i className="fas fa-info-circle me-2"></i>
                        No custom policies configured.
                      </td>
                    </tr>
                  ) : (
                    overrides.map((override, idx) => (
                      <tr key={idx}>
                        <td><code>{override.rule_code}</code></td>
                        <td>
                          <span className={override.override_type === 'SUPPRESS' ? 'text-warning' : 'text-info'}>
                            {override.override_type === 'SUPPRESS' ? 'Suppress' : 'Custom Value'}
                          </span>
                        </td>
                        <td>
                          {override.override_type === 'SUPPRESS' ? (
                            <span className="text-muted fst-italic">-</span>
                          ) : (
                            <span>{formatExpectedValue(override.custom_expected_value)}</span>
                          )}
                        </td>
                        <td>
                          {(override.account_types || ['all']).includes('all') ? (
                            <span className="text-muted">All accounts</span>
                          ) : (
                            <span className="small">
                              {(override.account_types || []).map(t =>
                                accountTypeOptions.find(o => o.value === t)?.label || t
                              ).join(', ')}
                            </span>
                          )}
                        </td>
                        <td className="small">{override.justification}</td>
                        <td>{override.expiry_date || 'Never'}</td>
                        <td>{override.created_by}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => {
                              setEditingOverride(override);
                              setOverrideForm({
                                rule_id: override.rule_id,
                                override_type: override.override_type,
                                custom_expected_value: override.custom_expected_value || '',
                                account_types: override.account_types || ['all'],
                                justification: override.justification,
                                expiry_date: override.expiry_date || ''
                              });
                              setShowOverrideModal(true);
                            }}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteOverride(override.id)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* User Compliance Tab */}
      {activeTab === 'users' && !loading && (
        <div className="card">
          <div className="card-header border-bottom" style={{ backgroundColor: '#e9ecef' }}>
            <h5 className="mb-0">
              <i className="fas fa-users me-2"></i>
              User Password Policy Compliance - Compared with Custom Policy
            </h5>
          </div>
          <div className="card-body">
            {/* Summary Table */}
            {userCompliance?.summary && (
              <div className="mb-4">
                <h6 className="mb-3" style={{ color: '#495057' }}>Summary by Account Type</h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead className="table-dark">
                      <tr>
                        <th>Account Type</th>
                        <th className="text-center">Total</th>
                        <th className="text-center">Auth: PASSWORD</th>
                        <th className="text-center">Compliant</th>
                        <th className="text-center">Non-Compliant</th>
                        <th className="text-center">N/A*</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: 'named', label: 'Named Accounts', icon: 'fa-user' },
                        { key: 'service', label: 'Service Accounts', icon: 'fa-cog' },
                        { key: 'system', label: 'System Accounts', icon: 'fa-database' }
                      ].map(({ key, label, icon }) => {
                        const s = userCompliance.summary[key] || {};
                        return (
                          <tr key={key}>
                            <td>
                              <i className={`fas ${icon} me-2 text-muted`}></i>
                              {label}
                            </td>
                            <td className="text-center">{s.total || 0}</td>
                            <td className="text-center">{s.password_auth || 0}</td>
                            <td className="text-center">
                              {s.compliant > 0 && <span className="text-success">{s.compliant}</span>}
                              {!s.compliant && <span className="text-muted">0</span>}
                            </td>
                            <td className="text-center">
                              {s.non_compliant > 0 && <span className="text-danger">{s.non_compliant}</span>}
                              {!s.non_compliant && <span className="text-muted">0</span>}
                            </td>
                            <td className="text-center text-muted">{s.na || 0}</td>
                          </tr>
                        );
                      })}
                      <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                        <td>Total</td>
                        <td className="text-center">{userCompliance.summary.total?.total || 0}</td>
                        <td className="text-center">{userCompliance.summary.total?.password_auth || 0}</td>
                        <td className="text-center text-success">{userCompliance.summary.total?.compliant || 0}</td>
                        <td className="text-center text-danger">{userCompliance.summary.total?.non_compliant || 0}</td>
                        <td className="text-center text-muted">{userCompliance.summary.total?.na || 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <small className="text-muted">
                  * N/A = Password policy not applicable (EXTERNAL, NONE, or GLOBAL authentication)
                </small>
              </div>
            )}

            {/* User List by Account Type */}
            {(() => {
              const users = userCompliance?.users || [];
              const grouped = {
                named: users.filter(u => u.account_type === 'named'),
                service: users.filter(u => u.account_type === 'service'),
                system: users.filter(u => u.account_type === 'system')
              };

              const accountTypeLabels = {
                named: { label: 'Named Accounts', icon: 'fa-user' },
                service: { label: 'Service Accounts', icon: 'fa-cog' },
                system: { label: 'System Accounts', icon: 'fa-database' }
              };

              return (
                <div className="accordion" id="userComplianceAccordion">
                  {Object.entries(grouped).map(([type, typeUsers]) => {
                    const { label, icon } = accountTypeLabels[type];
                    const isExpanded = expandedAccountTypes.includes(type);
                    const compliantCount = typeUsers.filter(u => u.is_compliant === true).length;
                    const nonCompliantCount = typeUsers.filter(u => u.is_compliant === false).length;

                    return (
                      <div className="accordion-item" key={type}>
                        <h2 className="accordion-header">
                          <button
                            className={`accordion-button ${!isExpanded ? 'collapsed' : ''}`}
                            type="button"
                            style={{ backgroundColor: '#e9ecef' }}
                            onClick={() => {
                              if (isExpanded) {
                                setExpandedAccountTypes(expandedAccountTypes.filter(t => t !== type));
                              } else {
                                setExpandedAccountTypes([...expandedAccountTypes, type]);
                              }
                            }}
                          >
                            <i className={`fas ${icon} me-2`}></i>
                            <strong>{label}</strong>
                            <span className="ms-2 text-muted small">({typeUsers.length})</span>
                            <span className="ms-auto me-3">
                              {compliantCount > 0 && <span className="text-success me-2">{compliantCount} compliant</span>}
                              {nonCompliantCount > 0 && <span className="text-danger">{nonCompliantCount} non-compliant</span>}
                            </span>
                          </button>
                        </h2>
                        <div className={`accordion-collapse collapse ${isExpanded ? 'show' : ''}`}>
                          <div className="accordion-body p-0">
                            <div className="table-responsive">
                              <table className="table table-sm table-hover mb-0">
                                <thead className="table-dark">
                                  <tr>
                                    <th>Username</th>
                                    <th>Database</th>
                                    <th>Profile</th>
                                    <th>Auth Type</th>
                                    <th>Status</th>
                                    <th>Compliance</th>
                                    {type === 'named' && <th>AD Info</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {typeUsers.length === 0 ? (
                                    <tr>
                                      <td colSpan={type === 'named' ? 7 : 6} className="text-center text-muted py-3">
                                        No users found
                                      </td>
                                    </tr>
                                  ) : (
                                    typeUsers.map((user, idx) => (
                                      <tr key={`${user.username}-${user.db_name}-${idx}`}>
                                        <td>
                                          <code>{user.username}</code>
                                        </td>
                                        <td>
                                          <small>{user.db_name}</small>
                                          {user.host_name && <small className="text-muted d-block">{user.host_name}</small>}
                                        </td>
                                        <td><small>{user.profile_name || '-'}</small></td>
                                        <td>
                                          {user.auth_type === 'PASSWORD' && <span className="text-primary">{user.auth_type}</span>}
                                          {user.auth_type === 'EXTERNAL' && <span className="text-info">{user.auth_type}</span>}
                                          {user.auth_type !== 'PASSWORD' && user.auth_type !== 'EXTERNAL' && <span className="text-muted">{user.auth_type || '-'}</span>}
                                        </td>
                                        <td>
                                          {user.account_status === 'OPEN' && <span className="text-success">OPEN</span>}
                                          {user.account_status === 'LOCKED' && <span className="text-danger">LOCKED</span>}
                                          {user.account_status !== 'OPEN' && user.account_status !== 'LOCKED' && <span className="text-muted">{user.account_status || '-'}</span>}
                                        </td>
                                        <td>
                                          {user.is_compliant === true && (
                                            <a
                                              href="#"
                                              className="text-success"
                                              style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                              onClick={(e) => { e.preventDefault(); setSelectedUserCompliance(user); }}
                                            >
                                              <i className="fas fa-check me-1"></i>
                                              {user.compliant_count}/{user.total_rules}
                                            </a>
                                          )}
                                          {user.is_compliant === false && (
                                            <a
                                              href="#"
                                              className="text-danger"
                                              style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                              onClick={(e) => { e.preventDefault(); setSelectedUserCompliance(user); }}
                                            >
                                              <i className="fas fa-times me-1"></i>
                                              {user.compliant_count}/{user.total_rules}
                                            </a>
                                          )}
                                          {user.is_compliant === null && (
                                            <span className="text-muted">N/A</span>
                                          )}
                                        </td>
                                        {type === 'named' && (
                                          <td>
                                            {user.enterprise_display_name && (
                                              <small>
                                                {user.enterprise_display_name}
                                                {user.department && <span className="text-muted"> ({user.department})</span>}
                                              </small>
                                            )}
                                            {!user.enterprise_display_name && <span className="text-muted">-</span>}
                                          </td>
                                        )}
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* User Compliance Detail Modal */}
      <WideModal
        show={!!selectedUserCompliance}
        onClose={() => setSelectedUserCompliance(null)}
        title={`Compliance Details: ${selectedUserCompliance?.username || ''}`}
        headerIcon="fa-clipboard-check"
        headerClass="bg-secondary text-white"
        maxWidth="900px"
        footer={
          <button className="btn btn-secondary" onClick={() => setSelectedUserCompliance(null)}>Close</button>
        }
      >
        {selectedUserCompliance && (
          <div>
            {/* User Info */}
            <div className="mb-3 p-3 bg-light rounded">
              <div className="row">
                <div className="col-md-4">
                  <small className="text-muted">Username</small>
                  <div><strong><code>{selectedUserCompliance.username}</code></strong></div>
                </div>
                <div className="col-md-4">
                  <small className="text-muted">Database</small>
                  <div>{selectedUserCompliance.db_name} <span className="text-muted">({selectedUserCompliance.host_name})</span></div>
                </div>
                <div className="col-md-4">
                  <small className="text-muted">Profile</small>
                  <div>{selectedUserCompliance.profile_name || '-'}</div>
                </div>
              </div>
            </div>

            {/* Compliance Summary */}
            <div className="mb-3">
              <span className="me-3">
                <i className="fas fa-check-circle text-success me-1"></i>
                <strong>{selectedUserCompliance.compliant_count}</strong> Compliant
              </span>
              <span>
                <i className="fas fa-times-circle text-danger me-1"></i>
                <strong>{selectedUserCompliance.non_compliant_count}</strong> Non-Compliant
              </span>
            </div>

            {/* Compliance Rules Table */}
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <thead className="table-dark">
                  <tr>
                    <th>Rule</th>
                    <th>Severity</th>
                    <th>Expected</th>
                    <th>Actual</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedUserCompliance.compliance_rules || []).map((rule, idx) => {
                    const expectedVal = (() => {
                      try {
                        const parsed = typeof rule.expected_value === 'string' ? JSON.parse(rule.expected_value) : rule.expected_value;
                        if (parsed?.operator && parsed?.value !== undefined) {
                          return `${parsed.operator} ${parsed.value}${parsed.unit ? ` ${parsed.unit}` : ''}`;
                        }
                        return parsed?.value ?? rule.expected_value;
                      } catch { return rule.expected_value; }
                    })();
                    const actualVal = (() => {
                      try {
                        const parsed = typeof rule.actual_value === 'string' ? JSON.parse(rule.actual_value) : rule.actual_value;
                        const val = parsed?.value;
                        if (val === null || val === undefined) return null;
                        return val;
                      } catch { return rule.actual_value; }
                    })();

                    return (
                      <tr key={idx}>
                        <td><code>{rule.rule_code}</code></td>
                        <td>
                          {rule.severity === 'CRITICAL' && <span className="text-danger">{rule.severity}</span>}
                          {rule.severity === 'HIGH' && <span className="text-danger">{rule.severity}</span>}
                          {rule.severity === 'MEDIUM' && <span className="text-warning">{rule.severity}</span>}
                          {rule.severity === 'LOW' && <span className="text-info">{rule.severity}</span>}
                        </td>
                        <td><small>{expectedVal}</small></td>
                        <td><small>{actualVal !== null ? actualVal : <span className="text-muted">NULL</span>}</small></td>
                        <td className="text-center">
                          {rule.is_compliant ? (
                            <span className="text-success"><i className="fas fa-check"></i></span>
                          ) : (
                            <span className="text-danger"><i className="fas fa-times"></i></span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </WideModal>

      {/* Profile Detail Modal */}
      <WideModal
        show={!!selectedProfile}
        onClose={() => setSelectedProfile(null)}
        title={`Profile Details: ${selectedProfile?.profile_name || ''}`}
        headerIcon="fa-user-shield"
        headerClass="bg-light border-bottom"
        maxWidth="1200px"
        footer={
          <button className="btn btn-secondary" onClick={() => setSelectedProfile(null)}>Close</button>
        }
      >
        {selectedProfile && (
          <div className="row">
            <div className={config.hasComplexityRules ? 'col-md-6' : 'col-12'}>
              <div className="card h-100">
                <div className="card-header bg-light border-bottom">
                  <h6 className="mb-0"><i className="fas fa-key me-2"></i>Password Policy</h6>
                </div>
                <div className="card-body p-0">
                  <table className="table table-sm table-striped mb-0">
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th style={{ width: '60%', whiteSpace: 'nowrap' }}>Setting</th>
                        <th style={{ width: '40%' }}>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(parseJSON(selectedProfile.password_policy)).map(([key, value]) => (
                        <tr key={key}>
                          <td className="text-capitalize" style={{ whiteSpace: 'nowrap' }}>{key.replace(/_/g, ' ')}</td>
                          <td><strong>{formatActualValue(value)}</strong></td>
                        </tr>
                      ))}
                      {Object.keys(parseJSON(selectedProfile.password_policy)).length === 0 && (
                        <tr><td colSpan="2" className="text-muted text-center">No policy data available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {config.hasComplexityRules && (
              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-header bg-light border-bottom">
                    <h6 className="mb-0"><i className="fas fa-shield-alt me-2"></i>Complexity Rules</h6>
                  </div>
                  <div className="card-body p-0">
                    <table className="table table-sm table-striped mb-0">
                      <thead style={{ backgroundColor: '#f8f9fa' }}>
                        <tr>
                          <th style={{ width: '60%', whiteSpace: 'nowrap' }}>Rule</th>
                          <th style={{ width: '40%' }}>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(parseJSON(selectedProfile.complexity_rules)).map(([key, value]) => (
                          <tr key={key}>
                            <td className="text-capitalize" style={{ whiteSpace: 'nowrap' }}>{key.replace(/_/g, ' ')}</td>
                            <td><strong>{formatActualValue(value)}</strong></td>
                          </tr>
                        ))}
                        {Object.keys(parseJSON(selectedProfile.complexity_rules)).length === 0 && (
                          <tr><td colSpan="2" className="text-muted text-center">No complexity rules defined</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </WideModal>

      {/* Custom Policy Modal */}
      <WideModal
        show={showOverrideModal}
        onClose={() => {
          setShowOverrideModal(false);
          setEditingOverride(null);
        }}
        title={editingOverride ? 'Edit Custom Policy' : 'Add Custom Policy'}
        headerIcon="fa-sliders-h"
        headerClass="bg-light border-bottom"
        maxWidth="900px"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowOverrideModal(false);
                setEditingOverride(null);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={saveOverride}
              disabled={!overrideForm.rule_id}
            >
              {editingOverride ? 'Update Policy' : 'Create Policy'}
            </button>
          </>
        }
      >
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label fw-bold">Benchmark Rule</label>
              <select
                className="form-select"
                value={overrideForm.rule_id}
                onChange={(e) => setOverrideForm({...overrideForm, rule_id: e.target.value})}
              >
                <option value="">Select a rule...</option>
                {benchmarkRules.map((rule) => (
                  <option key={rule.id} value={rule.id}>
                    {rule.rule_code} - {rule.rule_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Show selected rule details */}
            {overrideForm.rule_id && (
              <div className="alert alert-info mb-3">
                <strong>Current Requirement:</strong>{' '}
                {formatExpectedValue(benchmarkRules.find(r => r.id === parseInt(overrideForm.rule_id))?.expected_value)}
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-bold">Policy Type</label>
              <select
                className="form-select"
                value={overrideForm.override_type}
                onChange={(e) => setOverrideForm({...overrideForm, override_type: e.target.value})}
              >
                <option value="SUPPRESS">Suppress (Skip this rule entirely)</option>
                <option value="CUSTOM_VALUE">Custom Value (Use different requirement)</option>
              </select>
              <small className="text-muted">
                {overrideForm.override_type === 'SUPPRESS'
                  ? 'This rule will be skipped during compliance checks'
                  : 'Replace the standard requirement with your custom value'}
              </small>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Applicable Account Types</label>
              <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {accountTypeOptions.map((option) => (
                  <div key={option.value} className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`account-type-${option.value}`}
                      checked={overrideForm.account_types.includes(option.value)}
                      onChange={(e) => {
                        let newTypes;
                        if (option.value === 'all') {
                          newTypes = e.target.checked ? ['all'] : [];
                        } else {
                          const currentTypes = overrideForm.account_types.filter(t => t !== 'all');
                          if (e.target.checked) {
                            newTypes = [...currentTypes, option.value];
                          } else {
                            newTypes = currentTypes.filter(t => t !== option.value);
                          }
                          if (newTypes.length === 0) newTypes = ['all'];
                        }
                        setOverrideForm({...overrideForm, account_types: newTypes});
                      }}
                      disabled={option.value !== 'all' && overrideForm.account_types.includes('all')}
                    />
                    <label className="form-check-label" htmlFor={`account-type-${option.value}`}>
                      <strong>{option.label}</strong>
                      <br />
                      <small className="text-muted">{option.description}</small>
                    </label>
                  </div>
                ))}
              </div>
              <small className="text-muted">
                Select which account types this policy applies to. Select "All Accounts" for universal application, or choose specific types for differentiated requirements.
              </small>
            </div>
          </div>

          <div className="col-md-6">
            {overrideForm.override_type === 'CUSTOM_VALUE' && (
              <div className="mb-3">
                <label className="form-label fw-bold">Custom Expected Value</label>
                <div className="row g-2 mb-2">
                  <div className="col-md-5">
                    <label className="form-label small">Operator</label>
                    <select
                      className="form-select"
                      value={(() => {
                        try {
                          return JSON.parse(overrideForm.custom_expected_value || '{}').operator || '>=';
                        } catch { return '>='; }
                      })()}
                      onChange={(e) => {
                        let current = {};
                        try { current = JSON.parse(overrideForm.custom_expected_value || '{}'); } catch {}
                        current.operator = e.target.value;
                        setOverrideForm({...overrideForm, custom_expected_value: JSON.stringify(current)});
                      }}
                    >
                      <option value=">=">At least (≥)</option>
                      <option value="<=">At most (≤)</option>
                      <option value="==">Exactly (=)</option>
                      <option value="!=">Not equal (≠)</option>
                      <option value=">">Greater than (&gt;)</option>
                      <option value="<">Less than (&lt;)</option>
                      <option value="not_null">Must be configured</option>
                    </select>
                  </div>
                  <div className="col-md-7">
                    <label className="form-label small">Value</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g., 12, 90, UNLIMITED"
                      value={(() => {
                        try {
                          return JSON.parse(overrideForm.custom_expected_value || '{}').value || '';
                        } catch { return ''; }
                      })()}
                      onChange={(e) => {
                        let current = {};
                        try { current = JSON.parse(overrideForm.custom_expected_value || '{}'); } catch {}
                        // Always ensure operator is set (default to >=)
                        if (!current.operator) current.operator = '>=';
                        current.value = e.target.value;
                        setOverrideForm({...overrideForm, custom_expected_value: JSON.stringify(current)});
                      }}
                    />
                  </div>
                </div>
                <small className="text-muted">
                  Preview: <strong>{formatExpectedValue(overrideForm.custom_expected_value)}</strong>
                </small>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-bold">Justification</label>
              <textarea
                className="form-control"
                rows="4"
                placeholder="Explain why this custom policy is needed (e.g., business requirement, compensating control, legacy system limitation)..."
                value={overrideForm.justification}
                onChange={(e) => setOverrideForm({...overrideForm, justification: e.target.value})}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Expiry Date (Optional)</label>
              <input
                type="date"
                className="form-control"
                value={overrideForm.expiry_date}
                onChange={(e) => setOverrideForm({...overrideForm, expiry_date: e.target.value})}
              />
              <small className="text-muted">Leave empty for permanent policy. Expired policies will be flagged for review.</small>
            </div>
          </div>
        </div>
      </WideModal>
    </div>
  );
};

export default PasswordPolicyCompliance;
